import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert,
  Form, InputGroup, Modal, Dropdown
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import {
  listTools,
  updateToolCondition,
  getToolAssignmentHistory
} from '../../services/toolAssignmentService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';
import { INVENTORY_IMAGES_BUCKET, canonicalImageValue } from '../../utils/toolImageStorage';

const CONDITION_COLORS = { good: 'success', damaged: 'warning', lost: 'danger', maintenance: 'info' };
const CONDITION_LABELS = { good: 'Buena', damaged: 'Dañada', lost: 'Perdida', maintenance: 'Mantenimiento' };
const STATUS_COLORS = { available: 'success', assigned: 'primary', maintenance: 'warning', retired: 'secondary' };
const STATUS_LABELS = { available: 'Disponible', assigned: 'Asignada', maintenance: 'Mantenimiento', retired: 'Retirada' };

const registerTool = async ({ serial_number, name, category, brand, model, notes, image_url }) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('tools')
      .insert({
        serial_number: serial_number.trim(),
        name: name.trim(),
        category: category || null,
        brand: brand || null,
        model: model || null,
        notes: notes || null,
        // #568 — se persiste la RUTA del objeto, nunca una URL resuelta (una
        // URL firmada vence y la foto se muere sola, en silencio).
        image_url: canonicalImageValue(INVENTORY_IMAGES_BUCKET, image_url) || null,
        created_by: user?.id || null
      })
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

const ToolAssetsManager = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [registerModal, setRegisterModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [formError, setFormError] = useState('');

  const [newSerial, setNewSerial] = useState('');
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newNotes, setNewNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['all-tools', search, statusFilter],
    queryFn: () => listTools({
      search: search || undefined,
      status: statusFilter || undefined,
      limit: 200
    }),
    staleTime: 30 * 1000
  });

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['tool-history', historyModal?.id],
    queryFn: () => getToolAssignmentHistory(historyModal.id, { limit: 50 }),
    enabled: !!historyModal?.id
  });

  const registerMutation = useMutation({
    mutationFn: () => registerTool({
      serial_number: newSerial,
      name: newName,
      category: newCategory,
      brand: newBrand,
      model: newModel,
      notes: newNotes
    }),
    onSuccess: ({ error }) => {
      if (error) { setFormError(error.message); return; }
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      setRegisterModal(false);
      setNewSerial(''); setNewName(''); setNewCategory('');
      setNewBrand(''); setNewModel(''); setNewNotes('');
    },
    onError: (e) => setFormError(e.message)
  });

  const updateMutation = useMutation({
    mutationFn: () => updateToolCondition({
      tool_id: editModal.id,
      condition: editModal.condition,
      notes: editModal.notes || null
    }),
    onSuccess: ({ error }) => {
      if (error) { setFormError(error.message); return; }
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      setEditModal(null);
    },
    onError: (e) => setFormError(e.message)
  });

  const tools = data?.data || [];

  const stats = tools.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const openRegister = () => {
    setRegisterModal(true);
    setNewSerial(''); setNewName(''); setNewCategory('');
    setNewBrand(''); setNewModel(''); setNewNotes('');
    setFormError('');
  };

  if (!isAdmin) return <Alert variant="danger" className="m-3">Acceso restringido a administradores.</Alert>;

  return (
    <DashboardLayout>
      <Container fluid className="py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <Button variant="link" className="p-0 mb-1 text-muted small" onClick={() => navigate('/inventario')}>
              <i className="bi bi-arrow-left me-1" />Inventario
            </Button>
            <h2 className="modern-title mb-0">
              <i className="bi bi-tools me-2" />Catálogo de herramientas
            </h2>
          </div>
          <Button variant="primary" onClick={openRegister}>
            <i className="bi bi-plus-lg me-1" />Registrar herramienta
          </Button>
        </div>

        <Row className="g-2 mb-3">
          {Object.entries(STATUS_LABELS).map(([status, label]) => (
            <Col key={status} xs={6} sm={3}>
              <Card className="text-center py-2">
                <Card.Body className="p-2">
                  <div className="fw-bold fs-5">{stats[status] || 0}</div>
                  <Badge bg={STATUS_COLORS[status]} className="w-100">{label}</Badge>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-2 mb-3">
          <Col xs={12} sm={6} md={4}>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                placeholder="Buscar herramienta..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_LABELS).map(([s, l]) => (
                <option key={s} value={s}>{l}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {isLoading && <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}
        {!isLoading && tools.length === 0 && (
          <Alert variant="info">No hay herramientas registradas. Usa "Registrar herramienta" para agregar.</Alert>
        )}

        {!isLoading && tools.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Serial</th>
                  <th>Categoría</th>
                  <th>Marca/Modelo</th>
                  <th>Estado</th>
                  <th>Condición</th>
                  <th>Holder</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tools.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong></td>
                    <td><small className="font-monospace">{t.serial_number}</small></td>
                    <td>{t.category || '—'}</td>
                    <td>{t.brand || '—'}{t.model ? ` / ${t.model}` : ''}</td>
                    <td><Badge bg={STATUS_COLORS[t.status]}>{STATUS_LABELS[t.status]}</Badge></td>
                    <td><Badge bg={CONDITION_COLORS[t.condition]}>{CONDITION_LABELS[t.condition]}</Badge></td>
                    <td>
                      {t._active_assignment?.holder?.full_name || (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <KebabMenu label={`Acciones de ${t.name}`} stopPropagation={false}>
                          <Dropdown.Item onClick={() => { setEditModal({ ...t }); setFormError(''); }}>
                            <i className="bi bi-pencil me-2" />Editar condición
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setHistoryModal(t)}>
                            <i className="bi bi-clock-history me-2" />Ver historial
                          </Dropdown.Item>
                      </KebabMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Container>

      <Modal show={registerModal} onHide={() => setRegisterModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-plus-circle me-2" />Registrar nueva herramienta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                <Form.Control value={newName} onChange={e => setNewName(e.target.value)} placeholder="Taladro percutor" required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Serial <span className="text-danger">*</span></Form.Label>
                <Form.Control value={newSerial} onChange={e => setNewSerial(e.target.value)} placeholder="SN-12345" required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Categoría</Form.Label>
                <Form.Control value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Eléctrica, manual..." />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Marca</Form.Label>
                <Form.Control value={newBrand} onChange={e => setNewBrand(e.target.value)} placeholder="Bosch, DeWalt..." />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Modelo</Form.Label>
                <Form.Control value={newModel} onChange={e => setNewModel(e.target.value)} placeholder="GSB-13RE..." />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control as="textarea" rows={2} value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Condición inicial, accesorios..." />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRegisterModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!newSerial.trim() || !newName.trim() || registerMutation.isPending}
            onClick={() => registerMutation.mutate()}
          >
            {registerMutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
            Registrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editModal} onHide={() => setEditModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar: {editModal?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Condición</Form.Label>
            <Form.Select
              value={editModal?.condition || 'good'}
              onChange={e => setEditModal(prev => ({ ...prev, condition: e.target.value }))}
            >
              {Object.entries(CONDITION_LABELS).map(([s, l]) => (
                <option key={s} value={s}>{l}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea" rows={2}
              value={editModal?.notes || ''}
              onChange={e => setEditModal(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModal(null)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={updateMutation.isPending}
            onClick={() => updateMutation.mutate()}
          >
            {updateMutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!historyModal} onHide={() => setHistoryModal(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-clock-history me-2" />Historial: {historyModal?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistory && <div className="text-center"><Spinner animation="border" /></div>}
          {!loadingHistory && (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr><th>Técnico</th><th>Proyecto</th><th>Asignada</th><th>Devuelta</th><th>Notas</th></tr>
                </thead>
                <tbody>
                  {(historyData?.data || []).map(h => (
                    <tr key={h.id}>
                      <td>{h.holder?.full_name || '—'}</td>
                      <td>{h.project?.title || h.project?.project_number || '—'}</td>
                      <td>{new Date(h.assigned_at).toLocaleDateString('es-CR')}</td>
                      <td>{h.returned_at ? new Date(h.returned_at).toLocaleDateString('es-CR') : <span className="text-primary fw-bold">Activa</span>}</td>
                      <td><small className="text-muted">{h.notes || '—'}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!historyData?.data?.length && <p className="text-muted text-center">Sin historial previo.</p>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setHistoryModal(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default ToolAssetsManager;
